import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, UploadCloud, Wand2, AlertTriangle, RotateCcw } from 'lucide-react';
import { api } from '../../lib/api';
import { uploadCoachRecording } from '../../lib/coachApi';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import { useCoachT } from '../../lib/coachI18n';

const BusyBox = ({ call, t, onRetry }) => {
  if (call.status === 'error') {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm flex items-start gap-2" data-testid="coach-status-error">
        <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
        <div className="flex-1">{call.error}</div>
        {call.transcript && <Button size="sm" variant="outline" onClick={() => onRetry(call)} data-testid="coach-retry-btn"><RotateCcw className="w-3.5 h-3.5 mr-1" />{t('retry')}</Button>}
      </div>
    );
  }
  return (
    <div className="rounded-md border border-[#C9A227]/40 bg-[#C9A227]/10 p-3 text-sm flex items-center gap-3" data-testid="coach-status-busy">
      <Loader2 className="w-5 h-5 animate-spin text-[#8A6D12]" />
      <div>
        <div className="font-medium">{call.status === 'transcribing' ? t('transcribing') : t('analyzing')}</div>
        <div className="text-xs text-muted-foreground">{t('working')}</div>
      </div>
    </div>
  );
};

/* Tab "Analizza call": trascrizione incollata oppure registrazione audio/video */
export const CoachAnalyzeTab = ({ lead, latest, onStarted }) => {
  const { t, lang } = useCoachT();
  const [text, setText] = useState((lead.custom_fields || {}).show_transcript || '');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);
  const busy = latest && ['transcribing', 'analyzing', 'error'].includes(latest.status) ? latest : null;

  const analyze = async () => {
    if (text.trim().length < 200) return toast.error(t('minChars'));
    setSending(true);
    try {
      const r = await api.post(`/coach/leads/${lead.id}/analyze-text`, { transcript: text, lang });
      onStarted(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || t('error'));
    } finally {
      setSending(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setProgress(0);
    try {
      const call = await uploadCoachRecording({ leadId: lead.id, file, lang, onProgress: setProgress });
      onStarted(call);
    } catch (e) {
      toast.error(e.message || t('error'));
    } finally {
      setProgress(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const retry = async (call) => {
    await api.post(`/coach/calls/${call.id}/reanalyze`);
    onStarted({ ...call, status: 'analyzing' });
  };

  return (
    <div className="space-y-3" data-testid="coach-analyze-tab">
      {busy && <BusyBox call={busy} t={t} onRetry={retry} />}
      <div className="text-xs font-medium">{t('pasteLabel')}</div>
      <Textarea rows={7} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('pastePh')} className="font-mono text-[13px] bg-white/70" data-testid="coach-transcript-input" />
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={analyze} disabled={sending || progress !== null} className="bg-[#0F2A22] hover:bg-[#173C31] text-[#F4EBD0]" data-testid="coach-analyze-btn">
          {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1.5 text-[#C9A227]" />}{t('analyzeBtn')}
        </Button>
        <span className="text-xs text-muted-foreground">{t('orUpload')}</span>
        <input ref={fileRef} type="file" accept="audio/*,video/*,.m4a,.mp3,.wav,.mp4,.mov,.webm" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="coach-file-input" />
        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={progress !== null} data-testid="coach-upload-btn">
          <UploadCloud className="w-4 h-4 mr-1.5" />{t('chooseFile')}
        </Button>
      </div>
      {progress !== null && (
        <div className="space-y-1" data-testid="coach-upload-progress">
          <div className="text-xs">{t('uploading')} {progress}%</div>
          <Progress value={progress} className="h-2" />
        </div>
      )}
      <div className="text-[11px] text-muted-foreground">{t('uploadHint')}</div>
    </div>
  );
};
